import { FormattedMessage, useIntl } from 'react-intl';

import CursorClickIcon from '@/assets/icons/cursor-click.svg';
import { EmptyState } from '@/components/ui/empty-state';
import { Separator } from '@/components/ui/separator';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { cn } from '@/utils/classnames';

import { IneligibleOpenings } from './ineligible-openings';
import { OpeningRow } from './opening-row';

interface OpeningsPanelProps {
  className?: string;
}

/**
 * Right-side panel that lists the openings the user is posting to, lets them remove individual openings
 */
export function OpeningsPanel({ className }: OpeningsPanelProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { selectedOpeningIds, totalBudget, currentStep } =
    useJobPostingContext();
  const showEmptySelection =
    currentStep === 'select-openings' && selectedOpeningIds.length === 0;

  return (
    <aside
      className={cn(
        'border-fx-neutral-200 mb-6 flex w-[384px] shrink-0 flex-col overflow-hidden rounded-lg border bg-white shadow-sm',
        className,
      )}
    >
      {/* Header */}
      <header className="border-fx-neutral-200 bg-fx-neutral-100 border-b p-3">
        <Typography variant="body2">
          <FormattedMessage
            defaultMessage="Selected Openings ({count})"
            description="Header showing the count of openings in the job-posting flow"
            values={{ count: selectedOpeningIds.length }}
          />
        </Typography>
      </header>

      {showEmptySelection ? (
        <EmptyState
          icon={CursorClickIcon}
          iconClassName="size-6"
          className="w-full max-w-[328px]"
          header={intl.formatMessage({
            defaultMessage: 'No opening selected',
            description:
              'Selected openings rail: empty while the user is still picking openings',
          })}
          description={intl.formatMessage({
            defaultMessage:
              "Pick openings from the list to add them here. You'll set channels and spend for them in the next steps.",
            description:
              'Selected openings rail: explains that checked openings appear here before channels and spend',
          })}
          descriptionVariant="small1"
          descriptionClassName="text-fx-neutral-600"
        />
      ) : (
        <>
          <div className="flex-1 overflow-y-auto">
            <ul className="divide-fx-neutral-200 divide-y">
              {selectedOpeningIds.map(openingId => (
                <OpeningRow key={openingId} openingId={openingId} />
              ))}
            </ul>

            <Separator />

            <IneligibleOpenings />
          </div>

          {currentStep !== 'select-openings' && (
            <div className="mt-auto flex items-center justify-between p-4">
              <Typography variant="body1" className="text-fx-neutral-600">
                <FormattedMessage
                  defaultMessage="Total Spend"
                  description="Total budget of all openings"
                />
              </Typography>
              <Typography variant="h3">
                {formatCurrency(totalBudget)}
              </Typography>
            </div>
          )}
        </>
      )}
    </aside>
  );
}
